import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CircleCheck, Inbox, RefreshCw, Sunrise } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '../../../common/components/PageContainer';
import { EmptyState } from '../../../components/design-system/admin/EmptyState';
import { PillTabs } from '../../../components/design-system/admin/PillTabs';
import { readApiError } from '../../../common/utils/api-error';
import { DecisionActionId, DecisionItem, DecisionList, DecisionSource, useDecisionsApi } from '../../../common/hooks/use-decisions-api.hook';
import { DecisionRow, DecisionThumb, decisionTitle } from '../../../modules/vendor/decisions/DecisionRow';
import { SOURCE_ICON, SOURCE_ORDER, filterBySource, groupByUrgency, handledKey, isExternal, sourceKey } from '../../../modules/vendor/decisions/decision-presentation';

interface Handled {
  item: DecisionItem;
  action: DecisionActionId;
  until?: string;
}

/**
 * Everything waiting for the vendor's yes, from every corner of Ardealy: a
 * buyer's request, a hot lead, one of Ardi's proposals, an agent's planned
 * post, a content pack, a finding with something to do. Each row carries
 * its action; the yes or no goes to the module that owns the thing.
 */
export const DecisionsPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const api = useDecisionsApi();
  const navigate = useNavigate();
  const location = useLocation();

  const [list, setList] = useState<DecisionList | null>(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<DecisionSource | 'all'>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [handled, setHandled] = useState<Handled[]>([]);
  const focus = new URLSearchParams(location.search).get('item');

  const load = useCallback(() => {
    setFailed(false);
    return api.list(lang).then(setList).catch(() => setFailed(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    void load();
  }, [load]);

  // A link from the brief (?item=…) lands on its row.
  useEffect(() => {
    if (!focus || !list) return;
    document.getElementById(`decision-${focus}`)?.scrollIntoView({ block: 'center' });
  }, [focus, list]);

  const items = list?.items ?? [];
  const shown = useMemo(() => filterBySource(items, filter), [items, filter]);
  const groups = useMemo(() => groupByUrgency(shown), [shown]);
  const counts = useMemo(() => {
    const by: Partial<Record<DecisionSource, number>> = {};
    for (const i of items) by[i.source] = (by[i.source] ?? 0) + 1;
    return by;
  }, [items]);

  const remove = (id: string) => setList((l) => (l ? { ...l, items: l.items.filter((i) => i.id !== id) } : l));

  const act = async (item: DecisionItem, action: DecisionActionId) => {
    if (action === 'open') {
      if (isExternal(item.href)) window.open(item.href, '_blank', 'noopener');
      else navigate(item.href);
      return;
    }
    setBusy(`${item.id}:${action}`);
    try {
      const r = await api.act(item.id, action, { lang });
      if (r.ok) {
        remove(item.id);
        setHandled((h) => [{ item, action, until: r.until }, ...h].slice(0, 10));
        toast.success(t(handledKey(item, action)), { description: decisionTitle(t as any, item) });
      } else if (r.already || r.error === 'gone') {
        remove(item.id);
        toast(t(r.already ? 'DECISIONS.ALREADY' : 'DECISIONS.GONE'));
      } else {
        toast.error(t('DECISIONS.FAILED', { error: r.error ?? '' }));
      }
    } catch (err) {
      const body = await readApiError(err);
      toast.error(t('DECISIONS.FAILED', { error: body.message ? String(body.message) : '' }));
    } finally {
      setBusy(null);
    }
  };

  const filters = [
    { value: 'all' as const, label: t('DECISIONS.FILTER.ALL'), count: items.length },
    ...SOURCE_ORDER.filter((s) => counts[s]).map((s) => ({ value: s, label: t(`DECISIONS.FILTER.${s.toUpperCase()}`), count: counts[s] })),
  ];

  const nowCount = items.filter((i) => i.urgency === 'now').length;

  return (
    <PageContainer breadcrumbs={[{ label: t('AGENTS.NAV.HOME', 'Home'), href: '/home/dashboard' }, { label: t('R2R.NAV.DECISIONS') }]}>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6" data-testid="decisions-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-medium tracking-tight">{t('DECISIONS.TITLE')}</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              {list && items.length
                ? t('DECISIONS.SUMMARY', { count: items.length, now: nowCount })
                : t('DECISIONS.SUBTITLE')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => void load()} className="text-muted-foreground" aria-label={t('DECISIONS.REFRESH') as string}>
              <RefreshCw />
            </Button>
            <Button variant="outline" onClick={() => navigate('/home/brief')}>
              <Sunrise />
              {t('DECISIONS.BRIEF_LINK')}
            </Button>
          </div>
        </div>

        {failed && <p className="text-sm text-destructive">{t('DECISIONS.ERROR')}</p>}

        {!list && !failed && (
          <Card className="gap-0 py-0">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 border-b p-4 last:border-b-0">
                <Skeleton className="size-14 rounded-lg" />
                <div className="flex flex-1 flex-col gap-2">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            ))}
          </Card>
        )}

        {list && items.length > 0 && filters.length > 2 && (
          <PillTabs value={filter} options={filters} onChange={setFilter} ariaLabel={t('DECISIONS.FILTER.LABEL') as string} className="self-start" />
        )}

        {list && items.length === 0 && (
          <Card className="py-2">
            <EmptyState
              icon={<Inbox />}
              title={t('DECISIONS.EMPTY.TITLE')}
              description={t('DECISIONS.EMPTY.DESC')}
              className="border-0 pb-4"
            />
            <ul className="mx-auto grid w-full max-w-2xl gap-2 px-6 pb-8 sm:grid-cols-2" data-testid="decisions-watches">
              {SOURCE_ORDER.map((s) => {
                const Icon = SOURCE_ICON[s];
                return (
                  <li key={s} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border text-muted-foreground">
                      <Icon className="size-3.5" aria-hidden />
                    </span>
                    <span className="text-muted-foreground">{t(`DECISIONS.EMPTY.WATCHES.${s.toUpperCase()}`)}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        {groups.map((g) => (
          <section key={g.urgency} className="flex flex-col gap-2.5" data-testid={`decisions-${g.urgency}`}>
            <div className="flex items-baseline gap-2">
              <h2 className="text-sm font-medium">{t(`DECISIONS.GROUP.${g.urgency.toUpperCase()}`)}</h2>
              <span className="text-xs tabular-nums text-muted-foreground">{g.items.length}</span>
              <span className="text-xs text-muted-foreground">· {t(`DECISIONS.GROUP.${g.urgency.toUpperCase()}_HINT`)}</span>
            </div>
            <Card className="gap-0 py-0">
              <ul className="divide-y">
                {g.items.map((item) => (
                  <DecisionRow key={item.id} item={item} busy={busy} highlighted={focus === item.id} onAct={act} />
                ))}
              </ul>
            </Card>
          </section>
        ))}

        {handled.length > 0 && (
          <section className="flex flex-col gap-2.5" data-testid="decisions-handled">
            <h2 className="text-sm font-medium">{t('DECISIONS.HANDLED.TITLE')}</h2>
            <Card className="gap-0 py-0">
              <ul className="divide-y">
                {handled.map((h) => (
                  <li key={`${h.item.id}:${h.action}`} className="flex items-center gap-3 p-3">
                    <DecisionThumb item={h.item} className="size-9 opacity-80" />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CircleCheck className="size-3.5 text-success" aria-hidden />
                        {t(handledKey(h.item, h.action))}
                        {h.until && <> · {t('DECISIONS.HANDLED.UNTIL', { when: new Date(h.until).toLocaleString(lang, { weekday: 'long', hour: '2-digit', minute: '2-digit' }) })}</>}
                      </p>
                      <p className="truncate text-sm text-muted-foreground line-through decoration-muted-foreground/40">{decisionTitle(t as any, h.item)}</p>
                    </div>
                    <span className="hidden text-xs text-muted-foreground sm:inline">{t(sourceKey(h.item.source))}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </section>
        )}
      </div>
    </PageContainer>
  );
};

export default DecisionsPage;
